import _ from 'lodash';
import { type RestBranding } from '@bbkAdminRedux/rtkq/experience.slice';
import { type RestExperience } from '@bbkAdminUtils/api-client/rest-experiences-api';
import { GlobalLogger } from '@bbkAdminUtils/GlobalLogger';
import { type Paths } from '@bbkAdminUtils/harmful-types';
import {
  ThemeDefaults,
  ThemeKeys,
  ThemePaths,
  type CardLevelTheme,
  type EmotionTheme,
} from '@bbkFunnel/pages/theme';
import {
  DEFAULT_THEME_LABEL_DEFAULTS,
  getThemeLabelDefaults,
  type ThemeLabelDefaults,
} from '@bbkFunnelUtils/theme-label-defaults';

const getFontDefaultHref = (props: Pick<RestExperience, 'layout'>): string => {
  const basePath = 'layout.style_overlay';
  return _.get(
    props,
    `${basePath}.${ThemePaths.FONT_EMBED_URL}`,
    ThemeDefaults.FONT_EMBED_URL
  );
};

export const findElement: typeof document.querySelector = (
  selector: string
) => {
  try {
    return document.querySelector(selector);
  } catch (e) {
    GlobalLogger.error(e);
    return undefined;
  }
};

export const embedFont = (props: Pick<RestExperience, 'layout'>): void => {
  // check font href
  const href = getFontDefaultHref(props);
  if (href && href !== '') {
    if (!findElement(`link[href="${href}"]`)) {
      const link = document.createElement('link');
      link.href = href;
      link.rel = 'stylesheet';
      link.type = 'text/css';
      document.body?.appendChild(link);
    }
  }
};

export const prefetchImages = (props: Pick<RestExperience, 'layout'>): void => {
  const images: string[] = props.layout.cards
    .filter((card) => card.deploy === 'modal')
    .map(
      (card) =>
        _.get(card, 'requires.hero.url') ||
        _.get(card, 'requires.person.avatar')
    )
    .filter(Boolean);

  // delay to be after other resources
  setTimeout(() => {
    const links = images
      .filter((image) => !findElement(`link[href="${image}"]`))
      .map((image) => {
        const link = document.createElement('link');
        link.href = image;
        link.rel = 'prefetch';
        return link;
      });
    document.body?.append(...links);
  }, 1000);
};

const basePath: Paths<Pick<RestBranding, 'style_overlay'>, 0> = 'style_overlay';

// Merges `style_overlay` with already-resolved label defaults. Locale-aware
// resolution lives in `resolveFunnelTheme`, not here.
export const processTheme = ({
  layout,
  labelDefaults = DEFAULT_THEME_LABEL_DEFAULTS,
}: {
  layout: Pick<RestBranding, 'style_overlay'>;
  labelDefaults?: ThemeLabelDefaults;
}): EmotionTheme => {
  return {
    featureComparisonStatusLabels: {
      positive: labelDefaults.FEATURE_COMPARISON_INCLUDED,
      negative: labelDefaults.FEATURE_COMPARISON_NOT_INCLUDED,
    },
    [ThemeKeys.BACKGROUND]: _.get(
      layout,
      `${basePath}.${ThemePaths.BACKGROUND}`,
      ThemeDefaults.BACKGROUND
    ),
    [ThemeKeys.CARD_BACKGROUND]: _.get(
      layout,
      `${basePath}.${ThemePaths.CARD_BACKGROUND}`,
      ThemeDefaults.CARD_BACKGROUND
    ),
    [ThemeKeys.MODAL_BACKGROUND]: _.get(
      layout,
      `${basePath}.${ThemePaths.MODAL_BACKGROUND}`,
      ThemeDefaults.MODAL_BACKGROUND
    ),
    [ThemeKeys.FONT_FAMILY]: _.get(
      layout,
      `${basePath}.${ThemePaths.FONT_FAMILY}`,
      ThemeDefaults.FONT_FAMILY
    ),
    [ThemeKeys.FONT_EMBED_URL]: _.get(
      layout,
      `${basePath}.${ThemePaths.FONT_EMBED_URL}`,
      ThemeDefaults.FONT_EMBED_URL
    ),
    [ThemeKeys.FONT_SIZE]: _.get(
      layout,
      `${basePath}.${ThemePaths.FONT_SIZE}`,
      ThemeDefaults.FONT_SIZE
    ),
    [ThemeKeys.FONT_WEIGHT]: _.get(
      layout,
      `${basePath}.${ThemePaths.FONT_WEIGHT}`,
      ThemeDefaults.FONT_WEIGHT
    ),
    [ThemeKeys.PRIMARY_COLOR]:
      _.get(layout, `${basePath}.${ThemePaths.PRIMARY_COLOR}`) ||
      _.get(layout, `${basePath}.primary1Color`, ThemeDefaults.PRIMARY_COLOR),
    [ThemeKeys.TEXT_COLOR]: _.get(
      layout,
      `${basePath}.${ThemePaths.TEXT_COLOR}`,
      ThemeDefaults.TEXT_COLOR
    ),
    [ThemeKeys.BORDER_RADIUS]: _.get(
      layout,
      `${basePath}.${ThemePaths.BORDER_RADIUS}`,
      ThemeDefaults.BORDER_RADIUS
    ),
    [ThemeKeys.BUTTON_BORDER_RADIUS]: _.get(
      layout,
      `${basePath}.${ThemePaths.BUTTON_BORDER_RADIUS}`,
      ThemeDefaults.BUTTON_BORDER_RADIUS
    ),
    [ThemeKeys.BUTTON_TEXT_COLOR]: _.get(
      layout,
      `${basePath}.${ThemePaths.BUTTON_TEXT_COLOR}`,
      ThemeDefaults.BUTTON_TEXT_COLOR
    ),
    [ThemeKeys.MODAL_TEXT_COLOR]: _.get(
      layout,
      `${basePath}.${ThemePaths.MODAL_TEXT_COLOR}`,
      ThemeDefaults.MODAL_TEXT_COLOR
    ),
    [ThemeKeys.BUTTON_BACKGROUND_COLOR]: _.get(
      layout,
      `${basePath}.${ThemePaths.BUTTON_BACKGROUND_COLOR}`,
      ThemeDefaults.BUTTON_BACKGROUND_COLOR
    ),
    [ThemeKeys.BUTTON_TEXT_TRANSFORM]: _.get(
      layout,
      `${basePath}.${ThemePaths.BUTTON_TEXT_TRANSFORM}`,
      ThemeDefaults.BUTTON_TEXT_TRANSFORM
    ),
    [ThemeKeys.BUTTON_FONT_WEIGHT]: _.get(
      layout,
      `${basePath}.${ThemePaths.BUTTON_FONT_WEIGHT}`,
      ThemeDefaults.BUTTON_FONT_WEIGHT
    ),
    [ThemeKeys.BUTTON_FONT_SIZE]: _.get(
      layout,
      `${basePath}.${ThemePaths.BUTTON_FONT_SIZE}`,
      ThemeDefaults.BUTTON_FONT_SIZE
    ),
    [ThemeKeys.BUTTON_FONT_FAMILY]: _.get(
      layout,
      `${basePath}.${ThemePaths.BUTTON_FONT_FAMILY}`,
      ThemeDefaults.BUTTON_FONT_FAMILY
    ),
    [ThemeKeys.BOX_SHADOW]: _.get(
      layout,
      `${basePath}.${ThemePaths.BOX_SHADOW}`,
      ThemeDefaults.BOX_SHADOW
    ),
    [ThemeKeys.BORDER]: _.get(
      layout,
      `${basePath}.${ThemePaths.BORDER}`,
      ThemeDefaults.BORDER
    ),
    [ThemeKeys.ERROR_VAL]: _.get(
      layout,
      `${basePath}.${ThemePaths.ERROR_VAL}`,
      ThemeDefaults.ERROR_VAL
    ),
    [ThemeKeys.GLOBAL_CSS]: _.get(
      layout,
      `${basePath}.${ThemePaths.GLOBAL_CSS}`,
      ThemeDefaults.GLOBAL_CSS
    ),
    [ThemeKeys.LOGO_CENTER]: _.get(
      layout,
      `${basePath}.${ThemePaths.LOGO_CENTER}`,
      ThemeDefaults.LOGO_CENTER
    ),
    [ThemeKeys.LOGO_MAX_WIDTH]: _.get(
      layout,
      `${basePath}.${ThemePaths.LOGO_MAX_WIDTH}`,
      ThemeDefaults.LOGO_MAX_WIDTH
    ),
    [ThemeKeys.HEADER_BORDER]: _.get(
      layout,
      `${basePath}.${ThemePaths.HEADER_BORDER}`,
      ThemeDefaults.HEADER_BORDER
    ),
    [ThemeKeys.HEADER_FULL_WIDTH]: _.get(
      layout,
      `${basePath}.${ThemePaths.HEADER_FULL_WIDTH}`,
      ThemeDefaults.HEADER_FULL_WIDTH
    ),
    [ThemeKeys.TEXT_AREA_BORDER_RADIUS]: _.get(
      layout,
      `${basePath}.${ThemePaths.TEXT_AREA_BORDER_RADIUS}`,
      ThemeDefaults.TEXT_AREA_BORDER_RADIUS
    ),
    [ThemeKeys.TITLE_FONT_FAMILY]: _.get(
      layout,
      `${basePath}.${ThemePaths.TITLE_FONT_FAMILY}`,
      ThemeDefaults.TITLE_FONT_FAMILY
    ),
    [ThemeKeys.TITLE_FONT_SIZE]: _.get(
      layout,
      `${basePath}.${ThemePaths.TITLE_FONT_SIZE}`,
      ThemeDefaults.TITLE_FONT_SIZE
    ),
    [ThemeKeys.TITLE_FONT_WEIGHT]: _.get(
      layout,
      `${basePath}.${ThemePaths.TITLE_FONT_WEIGHT}`,
      ThemeDefaults.TITLE_FONT_WEIGHT
    ),
    [ThemeKeys.TITLE_FONT_COLOR]: _.get(
      layout,
      `${basePath}.${ThemePaths.TITLE_FONT_COLOR}`,
      ThemeDefaults.TITLE_FONT_COLOR
    ),
    [ThemeKeys.MODAL_TITLE_FONT_COLOR]: _.get(
      layout,
      `${basePath}.${ThemePaths.MODAL_TITLE_FONT_COLOR}`,
      ThemeDefaults.MODAL_TITLE_FONT_COLOR
    ),
    [ThemeKeys.TITLE_TEXT_TRANSFORM]: _.get(
      layout,
      `${basePath}.${ThemePaths.TITLE_TEXT_TRANSFORM}`,
      ThemeDefaults.TITLE_TEXT_TRANSFORM
    ),
    [ThemeKeys.SUBMIT_BUTTON_ORDER]: _.get(
      layout,
      `${basePath}.${ThemePaths.SUBMIT_BUTTON_ORDER}`,
      ThemeDefaults.SUBMIT_BUTTON_ORDER
    ),
    [ThemeKeys.SUBMIT_BUTTON_MODE]: _.get(
      layout,
      `${basePath}.${ThemePaths.SUBMIT_BUTTON_MODE}`,
      ThemeDefaults.SUBMIT_BUTTON_MODE
    ),
    [ThemeKeys.FORM_REQUIRED_LABEL]: _.get(
      layout,
      `${basePath}.${ThemePaths.FORM_REQUIRED_LABEL}`,
      labelDefaults.FORM_REQUIRED_LABEL
    ),
    [ThemeKeys.FORM_REQUIRED_FIELD_ERROR]: _.get(
      layout,
      `${basePath}.${ThemePaths.FORM_REQUIRED_FIELD_ERROR}`,
      labelDefaults.FORM_REQUIRED_FIELD_ERROR
    ),
    [ThemeKeys.SURVEY_ERROR_BOX_TITLE]: _.get(
      layout,
      `${basePath}.${ThemePaths.SURVEY_ERROR_BOX_TITLE}`,
      labelDefaults.SURVEY_ERROR_BOX_TITLE
    ),
    [ThemeKeys.SURVEY_ERROR_BOX_CONTENT]: _.get(
      layout,
      `${basePath}.${ThemePaths.SURVEY_ERROR_BOX_CONTENT}`,
      labelDefaults.SURVEY_ERROR_BOX_CONTENT
    ),
    [ThemeKeys.DICTIONARY_NONE]: _.get(
      layout,
      `${basePath}.${ThemePaths.DICTIONARY_NONE}`,
      ThemeDefaults.DICTIONARY_NONE
    ),
    [ThemeKeys.DICTIONARY_OTHER]: _.get(
      layout,
      `${basePath}.${ThemePaths.DICTIONARY_OTHER}`,
      ThemeDefaults.DICTIONARY_OTHER
    ),
    [ThemeKeys.OFFER_CARD_BADGE_COLORS]: _.get(
      layout,
      `${basePath}.${ThemePaths.OFFER_CARD_BADGE_COLORS}`,
      ThemeDefaults.OFFER_CARD_BADGE_COLORS
    ),
    [ThemeKeys.OFFER_CARD_BADGE_TEXT_COLORS]: _.get(
      layout,
      `${basePath}.${ThemePaths.OFFER_CARD_BADGE_TEXT_COLORS}`,
      ThemeDefaults.OFFER_CARD_BADGE_TEXT_COLORS
    ),
    [ThemeKeys.COMPLIANCE_EQUAL_BUTTON_SIZE]: _.get(
      layout,
      `${basePath}.${ThemePaths.COMPLIANCE_EQUAL_BUTTON_SIZE}`,
      ThemeDefaults.COMPLIANCE_EQUAL_BUTTON_SIZE
    ),
    [ThemeKeys.COMPLIANCE_CONSISTENT_BUTTON_STYLE]: _.get(
      layout,
      `${basePath}.${ThemePaths.COMPLIANCE_CONSISTENT_BUTTON_STYLE}`,
      ThemeDefaults.COMPLIANCE_CONSISTENT_BUTTON_STYLE
    ),
  };
};

// Single locale-aware boundary: resolves a raw locale into label defaults, then
// merges layout and optional card-level theme. Runtime paths should use this.
export const resolveFunnelTheme = ({
  layout,
  locale,
  cardLevelTheme,
}: {
  layout: Pick<RestBranding, 'style_overlay'>;
  locale?: string | null;
  cardLevelTheme?: CardLevelTheme;
}): EmotionTheme => ({
  ...processTheme({ layout, labelDefaults: getThemeLabelDefaults(locale) }),
  [ThemeKeys.CARD_LEVEL_THEME]: cardLevelTheme,
});

// Resolves the page-load-modal card's style_overlay into a card-level theme.
export const getPageLoadModalCardTheme = (
  layout: { cards: Array<{ id?: string }> },
  config?: { page_load_modal?: { card_id?: string } } | null
): CardLevelTheme | undefined => {
  const modalCardId = config?.page_load_modal?.card_id;
  if (!modalCardId) {
    return undefined;
  }
  return _.get(
    layout.cards.find((card) => card.id === modalCardId),
    'style_overlay',
    undefined
  ) as CardLevelTheme | undefined;
};
